import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { useTranslation } from "react-i18next";

import Header from "../../components/Header/Header";
import WaitlistForm from "../../components/WaitlistForm/WaitlistForm.jsx";
import AIPlatformHeroFeatures from "./AIPlatformHeroFeatures";
import "./AIPlatform.css";
import avatarImage from "../../assets/avatar.webp";
import userImage from "../../assets/user.webp";
import virtualAssistantImage from "../../assets/virtual-assistants.webp";

const HERO_FEATURE_TO_PILLAR = {
  workflows: "tasks",
  mcp: "connections",
  avatar: "avatar",
};

gsap.registerPlugin(ScrollTrigger);

export default function AIPlatform() {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const orbitRef = useRef(null);
  const heroRef = useRef(null);
  const detailPanelRef = useRef(null);
  const pillarsRef = useRef(null);
  const waitlistRef = useRef(null);
  const featureRefs = useRef([]);
  const features = t("aiPlatform.features", { returnObjects: true });
  const heroFeatures = t("aiPlatform.hero.features", { returnObjects: true });
  const safeFeatures = useMemo(() => (Array.isArray(features) ? features : []), [features]);
  const safeHeroFeatures = useMemo(
    () => (Array.isArray(heroFeatures) ? heroFeatures : []),
    [heroFeatures]
  );
  const [activeHeroFeatureId, setActiveHeroFeatureId] = useState("workflows");
  const [activeFeatureId, setActiveFeatureId] = useState("avatar");

  const activeFeature = useMemo(
    () =>
      safeFeatures.find((feature) => feature.id === activeFeatureId) ??
      safeFeatures[0] ??
      { label: "", title: "", description: "", bullets: [], pulse: "" },
    [activeFeatureId, safeFeatures]
  );

  const activeScenario = useMemo(() => {
    const scenarios = {
      avatar: {
        leftTitle: "Basic information",
        leftFields: ["Avatar image", "Assistant name", "Description"],
        rightTitle: "Avatar sections",
        rightFields: ["Language", "Output format"],
        pills: ["Add section", "Delete last"],
        toggles: ["Private visibility", "Team access"],
      },
      agent: {
        leftTitle: "Assistant profile",
        leftFields: ["Name", "Description", "Skills"],
        rightTitle: "Model configuration",
        rightFields: ["LLM model", "Temperature", "Max tokens"],
        pills: ["GPT-4o mini", "Balanced mode"],
        toggles: ["Memory", "Draft mode"],
      },
      skills: {
        leftTitle: "Skill library",
        leftFields: ["Skill name", "Input", "Output"],
        rightTitle: "Execution profile",
        rightFields: ["Validator", "Fallback", "Run policy"],
        pills: ["Weather API", "CRM Query"],
        toggles: ["Auto retry", "Manual approval"],
      },
      connections: {
        leftTitle: "Connected apps",
        leftFields: ["PostgreSQL", "GitLab", "Gmail"],
        rightTitle: "Credentials",
        rightFields: ["Host", "Project ID", "Secret key"],
        pills: ["MCP enabled", "Real-time sync"],
        toggles: ["Encrypt values", "Live refresh"],
      },
      tasks: {
        leftTitle: "Workflow builder",
        leftFields: ["Trigger", "Condition", "Action"],
        rightTitle: "Execution",
        rightFields: ["Schedule", "Retries", "Priority"],
        pills: ["Auto run", "With approvals"],
        toggles: ["Monitoring", "Alerts"],
      },
    };

    return scenarios[activeFeatureId] ?? scenarios.avatar;
  }, [activeFeatureId]);

  useLayoutEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const lenis = new Lenis({
      duration: 1.1,
      smoothWheel: true,
      wheelMultiplier: 0.95,
      touchMultiplier: 1.2,
    });

    let frameId = 0;

    const raf = (time) => {
      lenis.raf(time);
      frameId = window.requestAnimationFrame(raf);
    };

    lenis.on("scroll", ScrollTrigger.update);
    frameId = window.requestAnimationFrame(raf);

    const context = gsap.context(() => {
      gsap.fromTo(
        ".ai-platform-reveal",
        { autoAlpha: 0, y: 42 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          stagger: 0.08,
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
          },
        }
      );

      gsap.fromTo(
        heroRef.current,
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 1, ease: "power3.out" }
      );

      gsap.fromTo(
        orbitRef.current,
        { autoAlpha: 0, y: 28 },
        { autoAlpha: 1, y: 0, duration: 1, delay: 0.08, ease: "power3.out" }
      );

      gsap.to(".ai-platform-command-grid", {
        rotate: 360,
        duration: 34,
        repeat: -1,
        ease: "none",
        transformOrigin: "50% 50%",
      });

      gsap.to(".ai-platform-command-glow", {
        scale: 1.08,
        opacity: 0.85,
        duration: 3.2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".ai-platform-radar-ring--outer", {
        scaleX: 0.985,
        scaleY: 1.04,
        rotation: -10,
        opacity: 0.88,
        duration: 5.2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        transformOrigin: "50% 50%",
      });

      gsap.to(".ai-platform-radar-ring--mid", {
        scaleX: 1.02,
        scaleY: 0.98,
        rotation: 9,
        opacity: 0.72,
        duration: 4.6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        transformOrigin: "50% 50%",
      });

      gsap.to(".ai-platform-radar-ring--inner", {
        scaleX: 0.98,
        scaleY: 1.03,
        rotation: -14,
        opacity: 0.66,
        duration: 4,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        transformOrigin: "50% 50%",
      });

      gsap.to(".ai-platform-hero-node--1", {
        y: -12,
        duration: 3.2,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".ai-platform-hero-node--2", {
        y: 10,
        x: 8,
        duration: 3.6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".ai-platform-hero-node--3", {
        y: -8,
        x: -6,
        duration: 3.1,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".ai-platform-hero-panel", {
        y: -6,
        duration: 3.8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.fromTo(
        ".ai-platform-feature-card",
        { autoAlpha: 0, y: 48 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.08,
          scrollTrigger: {
            trigger: pillarsRef.current,
            start: "top 70%",
          },
        }
      );

      gsap.fromTo(
        ".ai-platform-waitlist-card",
        { autoAlpha: 0, y: 50 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: waitlistRef.current,
            start: "top 78%",
          },
        }
      );

      ScrollTrigger.refresh();
    }, containerRef);

    return () => {
      window.cancelAnimationFrame(frameId);
      lenis.destroy();
      context.revert();
    };
  }, []);

  useEffect(() => {
    if (!detailPanelRef.current) {
      return undefined;
    }

    const detailAnimation = gsap.timeline();
    detailAnimation.fromTo(
      detailPanelRef.current,
      { autoAlpha: 0, y: 24, scale: 0.985 },
      { autoAlpha: 1, y: 0, scale: 1, duration: 0.42, ease: "power3.out" }
    );

    featureRefs.current.forEach((element, index) => {
      if (!element) {
        return;
      }

      const isActive = safeFeatures[index]?.id === activeFeatureId;
      gsap.to(element, {
        y: isActive ? -10 : 0,
        scale: isActive ? 1.015 : 1,
        duration: 0.28,
        ease: "power2.out",
      });
    });

    return () => {
      detailAnimation.kill();
    };
  }, [activeFeatureId, safeFeatures]);

  const scrollToSection = (sectionRef) => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const setFeatureRef = (index) => (element) => {
    featureRefs.current[index] = element;
  };

  const handleHeroFeatureSelect = (featureId) => {
    setActiveHeroFeatureId(featureId);

    const mappedFeature = HERO_FEATURE_TO_PILLAR[featureId];
    if (mappedFeature) {
      setActiveFeatureId(mappedFeature);
    }
  };

  return (
    <div ref={containerRef} className="ai-platform-page">
      <div className="ai-platform-noise" />
      <div className="ai-platform-aurora" />
      <div className="ai-platform-grid" />

      <div className="ai-platform-shell">
        <Header />

        <section className="ai-platform-section ai-platform-hero">
          <div ref={heroRef} className="ai-platform-copy">
            <div className="ai-platform-kicker">
              <span className="ai-platform-kicker-dot" />
              {t("aiPlatform.kicker")}
            </div>

            <h1 className="ai-platform-title">
              <span>{t("aiPlatform.title.line1")}</span>
              <span>{t("aiPlatform.title.line2")}</span>
              <span className="ai-platform-title-accent">{t("aiPlatform.title.line3")}</span>
              <span className="ai-platform-title-accent">{t("aiPlatform.title.line4")}</span>
            </h1>

            <p className="ai-platform-subtitle">{t("aiPlatform.subtitle")}</p>

            <div className="ai-platform-actions">
              <button
                type="button"
                className="ai-platform-button ai-platform-button--primary"
                onClick={() => scrollToSection(waitlistRef)}
              >
                {t("aiPlatform.actions.joinWaitlist")}
              </button>

              <button
                type="button"
                className="ai-platform-button ai-platform-button--secondary"
                onClick={() => scrollToSection(pillarsRef)}
              >
                {t("aiPlatform.actions.howItWorks")}
              </button>
            </div>

          </div>

          <div ref={orbitRef} className="ai-platform-orbit">
            <div className="ai-platform-command-glow" />
            <div className="ai-platform-command-grid" />
            <div className="ai-platform-radar">
              <div className="ai-platform-radar-ring ai-platform-radar-ring--outer" />
              <div className="ai-platform-radar-ring ai-platform-radar-ring--mid" />
              <div className="ai-platform-radar-ring ai-platform-radar-ring--inner" />
            </div>

            <AIPlatformHeroFeatures
              features={safeHeroFeatures}
              activeFeatureId={activeHeroFeatureId}
              onFeatureSelect={handleHeroFeatureSelect}
            />
          </div>
        </section>

        <section ref={pillarsRef} className="ai-platform-section ai-platform-points">
          <div className="ai-platform-section-heading ai-platform-reveal">
            <h2 className="ai-platform-section-title">{t("aiPlatform.pillars.title")}</h2>
            <p className="ai-platform-section-description">{t("aiPlatform.pillars.description")}</p>
          </div>

          <div className="ai-platform-points-layout">
            <div className="ai-platform-feature-grid">
              {safeFeatures.map((feature, index) => (
                <button
                  key={feature.id}
                  ref={setFeatureRef(index)}
                  type="button"
                  className={`ai-platform-feature-card ${
                    feature.id === activeFeatureId ? "is-active" : ""
                  }`}
                  onClick={() => setActiveFeatureId(feature.id)}
                >
                  <span className="ai-platform-feature-index">0{index + 1}</span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </button>
              ))}
            </div>

            <aside ref={detailPanelRef} className="ai-platform-detail">
              <div className="ai-platform-laptop-wrap">
                <div key={activeFeatureId} className={`ai-platform-laptop ai-platform-laptop--${activeFeatureId}`}>
                  <div className="ai-platform-laptop-screen">
                    <div className="ai-platform-laptop-screen-top">
                      <span className="ai-platform-laptop-chip">{activeFeature.label}</span>
                    </div>

                    {activeFeatureId === "avatar" ? (
                      <div className="ai-platform-sim-avatar">
                        <div className="ai-platform-sim-avatar-toolbar">
                          <span className="ai-platform-sim-avatar-pill">Identity</span>
                          <button
                            type="button"
                            className="ai-platform-sim-avatar-button ai-platform-click-target ai-platform-click-target--avatar-1"
                          >
                            Change Avatar
                          </button>
                        </div>

                        <div className="ai-platform-sim-avatar-layout">
                          <article className="ai-platform-sim-avatar-card ai-platform-sim-avatar-card--identity">
                            <strong>Avatar profile</strong>
                            <div className="ai-platform-sim-avatar-hero">
                              <img src={avatarImage} alt="Avatar option" className="ai-platform-sim-avatar-hero-image" />
                            </div>
                            <div className="ai-platform-sim-avatar-field">
                              <span>Avatar name</span>
                              <i className="ai-platform-click-target ai-platform-click-target--avatar-2" />
                            </div>
                            <div className="ai-platform-sim-avatar-field">
                              <span>Description</span>
                              <i />
                            </div>
                            <div className="ai-platform-sim-avatar-tags">
                              <span className="ai-platform-click-target ai-platform-click-target--avatar-3">support</span>
                              <span>sales</span>
                              <span>multilingual</span>
                            </div>
                          </article>

                          <article className="ai-platform-sim-avatar-card">
                            <strong>Avatar gallery</strong>
                            <div className="ai-platform-sim-avatar-gallery">
                              <button type="button" className="ai-platform-sim-avatar-thumb is-active">
                                <img src={avatarImage} alt="Avatar style one" />
                                <span>Core</span>
                              </button>
                              <button type="button" className="ai-platform-sim-avatar-thumb">
                                <img src={userImage} alt="Avatar style two" />
                                <span>Human</span>
                              </button>
                              <button type="button" className="ai-platform-sim-avatar-thumb">
                                <img src={virtualAssistantImage} alt="Avatar style three" />
                                <span>Pro</span>
                              </button>
                            </div>
                            <div className="ai-platform-sim-avatar-actions">
                              <button
                                type="button"
                                className="ai-platform-sim-avatar-button ai-platform-sim-avatar-button--ghost ai-platform-click-target ai-platform-click-target--avatar-4"
                              >
                                Save avatar
                              </button>
                              <button type="button" className="ai-platform-sim-avatar-button ai-platform-sim-avatar-button--muted">
                                Randomize
                              </button>
                            </div>
                          </article>
                        </div>
                      </div>
                    ) : activeFeatureId === "skills" ? (
                      <div className="ai-platform-sim-modal">
                        <div className="ai-platform-sim-modal-search">Search skills...</div>
                        <div className="ai-platform-sim-skill-grid">
                          <article className="ai-platform-sim-skill-card is-selected">
                            <strong>Weather Information</strong>
                            <p>Get weather data for any location in real time.</p>
                            <span>Learn more</span>
                          </article>
                          <article className="ai-platform-sim-skill-card is-selected">
                            <strong>GAM Classifier</strong>
                            <p>Classify locations and users by contextual rules.</p>
                            <span>Learn more</span>
                          </article>
                          <article className="ai-platform-sim-skill-card">
                            <strong>Web Search</strong>
                            <p>Search public information, news, and references.</p>
                            <span>Learn more</span>
                          </article>
                        </div>
                      </div>
                    ) : activeFeatureId === "connections" ? (
                      <div className="ai-platform-sim-modal ai-platform-sim-modal--connections">
                        <strong className="ai-platform-sim-modal-title">Add New Connection</strong>
                        <p className="ai-platform-sim-modal-subtitle">
                          Choose a platform to connect with your account.
                        </p>

                        <div className="ai-platform-sim-connection-list">
                          <div className="ai-platform-sim-connection-item">
                            <b>Meta</b>
                            <span>Connect WhatsApp, Instagram, or Facebook</span>
                          </div>
                          <div className="ai-platform-sim-connection-item">
                            <b>Shopify</b>
                            <span>Sync products and orders automatically.</span>
                          </div>
                          <div className="ai-platform-sim-connection-item">
                            <b>Google</b>
                            <span>Connect Gmail, Calendar, and Drive services.</span>
                          </div>
                        </div>
                      </div>
                    ) : activeFeatureId === "tasks" ? (
                      <div className="ai-platform-sim-task-board">
                        <div className="ai-platform-sim-task-composer">
                          <span>New task</span>
                          <div className="ai-platform-sim-task-composer-row">
                            <i className="ai-platform-click-target ai-platform-click-target--tasks-1" />
                            <button type="button" className="ai-platform-sim-task-add ai-platform-click-target ai-platform-click-target--tasks-2">
                              + Add
                            </button>
                          </div>
                        </div>

                        <div className="ai-platform-sim-task-columns">
                          <article className="ai-platform-sim-task-column">
                            <strong>To do</strong>
                            <div className="ai-platform-sim-task-card ai-platform-click-target ai-platform-click-target--tasks-3">
                              Design onboarding flow
                            </div>
                            <div className="ai-platform-sim-task-card">Define core prompts</div>
                          </article>

                          <article className="ai-platform-sim-task-column">
                            <strong>In progress</strong>
                            <div className="ai-platform-sim-task-card is-accent">Integrate CRM webhook</div>
                            <div className="ai-platform-sim-task-card">Validation testing</div>
                          </article>

                          <article className="ai-platform-sim-task-column">
                            <strong>Completed</strong>
                            <div className="ai-platform-sim-task-card">Set up board</div>
                            <div className="ai-platform-sim-task-card ai-platform-click-target ai-platform-click-target--tasks-4">
                              Publish weekly workflow
                            </div>
                          </article>
                        </div>
                      </div>
                    ) : (
                      <div className="ai-platform-laptop-layout">
                        <div className="ai-platform-laptop-panel">
                          <strong>{activeScenario.leftTitle}</strong>
                          {activeScenario.leftFields.map((field) => (
                            <div key={field} className="ai-platform-laptop-field">
                              <span>{field}</span>
                              <i />
                            </div>
                          ))}

                          <div className="ai-platform-laptop-pills">
                            {activeScenario.pills.map((pill) => (
                              <b key={pill}>{pill}</b>
                            ))}
                          </div>
                        </div>

                        <div className="ai-platform-laptop-panel ai-platform-laptop-panel--right">
                          <strong>{activeScenario.rightTitle}</strong>
                          {activeScenario.rightFields.map((field) => (
                            <div key={field} className="ai-platform-laptop-field">
                              <span>{field}</span>
                              <i />
                            </div>
                          ))}

                          <div className="ai-platform-laptop-toggles">
                            {activeScenario.toggles.map((toggle, index) => (
                              <div key={toggle} className="ai-platform-laptop-toggle">
                                <span>{toggle}</span>
                                <em className={index === 0 ? "is-on" : ""} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    <span
                      className={`ai-platform-laptop-cursor ai-platform-laptop-cursor--${activeFeatureId}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ai-platform-laptop-hinge" aria-hidden="true" />
                  <div className="ai-platform-laptop-base" aria-hidden="true">
                    <div className="ai-platform-laptop-trackpad" />
                  </div>
                  <div className="ai-platform-laptop-shadow" aria-hidden="true" />
                </div>
              </div>

              <span className="ai-platform-detail-label">{activeFeature.label}</span>
              <h3>{activeFeature.title}</h3>
              <p>{activeFeature.description}</p>

              <div className="ai-platform-detail-list">
                {activeFeature.bullets.map((bullet) => (
                  <div key={bullet} className="ai-platform-detail-item">
                    <i />
                    <span>{bullet}</span>
                  </div>
                ))}
              </div>

              <div className="ai-platform-detail-pulse">
                <strong>{t("aiPlatform.pillars.pulseTitle")}</strong>
                <p>{activeFeature.pulse}</p>
              </div>
            </aside>
          </div>
        </section>

        <section ref={waitlistRef} className="ai-platform-section ai-platform-waitlist">
          <div className="ai-platform-waitlist-card">
            <h2>{t("aiPlatform.waitlist.title")}</h2>
            <p>{t("aiPlatform.waitlist.description")}</p>

            <div className="ai-platform-waitlist-form">
              <WaitlistForm
                layout="stacked"
                placeholder={t("aiPlatform.waitlist.placeholder")}
                buttonText={t("aiPlatform.waitlist.buttonText")}
                loadingText={t("aiPlatform.waitlist.loadingText")}
                successText={t("aiPlatform.waitlist.successText")}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
